import { initializeApp } from "firebase/app";
import {
  doc,
  getDoc,
  getFirestore,
  updateDoc,
  setDoc,
  arrayUnion,
  deleteField,
} from "firebase/firestore";
import { collection, getDocs } from "firebase/firestore";
import { getStorage, ref, getDownloadURL, uploadBytes, deleteObject, getBlob } from "firebase/storage";
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged } from "firebase/auth";
import { getJson } from "./config";

// ─── Environment ─────────────────────────────────────────────────────────────

export const isUat = () =>
  process.env.REACT_APP_ENV === "uat" ||
  (process.env.REACT_APP_ENV === undefined && window.location.hostname === "localhost");

export const getDocNames = () =>
  isUat()
    ? { info: "InfoTest", urls: "UrlsTest", admins: "AdminsTest" }
    : { info: "Info", urls: "Urls", admins: "Admins" };

// ─── Firebase init ────────────────────────────────────────────────────────────

let _app = null;

export const init = () => {
  if (_app) return _app;
  const firebaseConfig = {
    apiKey: process.env.REACT_APP_API_KEY,
    authDomain: process.env.REACT_APP_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_PROJECT_ID,
    storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_APP_ID,
  };
  _app = initializeApp(firebaseConfig);
  if (!navigator.onLine) {
    alert("Not Connected To Network");
  }
  return _app;
};

// ─── Firestore data fetch (used by App.js on load) ───────────────────────────

export const getFirestoreData = async () => {
  const app = init();
  const db = getFirestore(app);
  const { info } = getDocNames();
  const docRef = doc(db, "Data", info);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    const data = docSnap.data();
    sessionStorage.setItem("info", JSON.stringify(data));
    return true;
  } else {
    console.log("No such document!");
    alert("Server Error !");
  }
  return false;
};

export const getFirestoreUrls = async () => {
  const app = init();
  const db = getFirestore(app);
  const { urls } = getDocNames();
  const docRef = doc(db, "Data", urls);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    const data = docSnap.data();
    sessionStorage.setItem("urls", JSON.stringify(data));
    return true;
  } else {
    console.log("No such document!");
    alert("Server Error !");
  }
  return false;
};

// ─── Image URL resolution ─────────────────────────────────────────────────────

export const mangleKey = (imagePath) => imagePath.replace(/[./_\-]/g, "");

export const getImage = (imagePath) => {
  if (getJson("firebaseStorage") === true) {
    if (imagePath != undefined) {
      init();
      const modifiedPath = mangleKey(imagePath);
      const dataString = sessionStorage.getItem("urls");
      const dataObj = JSON.parse(dataString);
      if (!dataObj) return "";
      const obj = dataObj[modifiedPath];
      return obj?.Url;
    } else {
      console.warn("Image Could Not Be Found From Firebase Storage!");
      return "";
    }
  }
};

export const getFileURL = async (path) => {
  init();
  const storage = getStorage();
  return getDownloadURL(ref(storage, path));
};

// Download a stored image as a Blob using the Firebase SDK.
// Using the SDK (getBlob) instead of fetch() avoids browser CORS restrictions
// since Firebase handles the request internally with its own auth headers.
export const downloadImageAsBlob = async (logicalPath) => {
  const app = init();
  const storage = getStorage(app);
  // Fail fast (~8s) if the request is blocked (e.g. missing bucket CORS) instead
  // of the SDK's default ~2min retry storm, so the UI can surface a clear error.
  storage.maxOperationRetryTime = 8000;
  const storagePath = isUat() ? `uat/${logicalPath}` : logicalPath;
  return getBlob(ref(storage, storagePath));
};

// ─── Auth ─────────────────────────────────────────────────────────────────────

export const getAuthInstance = () => {
  const app = init();
  return getAuth(app);
};

export const signInWithGoogle = async () => {
  const auth = getAuthInstance();
  const provider = new GoogleAuthProvider();
  const result = await signInWithPopup(auth, provider);
  return result.user;
};

export const signOutUser = () => signOut(getAuthInstance());

export const subscribeToAuth = (cb) => onAuthStateChanged(getAuthInstance(), cb);

// ─── Admin helpers ────────────────────────────────────────────────────────────

export const getAdminEmails = async () => {
  const app = init();
  const db = getFirestore(app);
  const { admins } = getDocNames();
  const snap = await getDoc(doc(db, "Data", admins));
  if (snap.exists()) return snap.data().emails || [];
  return [];
};

export const addAdminEmail = async (email) => {
  const app = init();
  const db = getFirestore(app);
  const { admins } = getDocNames();
  const ref_ = doc(db, "Data", admins);
  const snap = await getDoc(ref_);
  if (snap.exists()) {
    await updateDoc(ref_, { emails: arrayUnion(email) });
  } else {
    await setDoc(ref_, { emails: [email] });
  }
};

export const updateTeamData = async (teamArray) => {
  const app = init();
  const db = getFirestore(app);
  const { info } = getDocNames();
  await updateDoc(doc(db, "Data", info), { team: teamArray });
  // refresh sessionStorage
  const snap = await getDoc(doc(db, "Data", info));
  if (snap.exists()) sessionStorage.setItem("info", JSON.stringify(snap.data()));
};

// ─── Image upload (UAT: uat/ prefix + manual Urls entry; Prod: serverless handles Urls) ──

// Cropped thumbnail path — always JPEG regardless of source format.
export const pathForMember = (name, categories) => {
  const clean = name.replace(/Dr\.\s*/g, "").replace(/\s+/g, "");
  let folder = "Volunteers";
  if (categories.includes("Doctor")) folder = "Physicians";
  else if (categories.includes("Admin")) folder = "Admins";
  else if (categories.includes("Lead")) folder = "Leads";
  return `images/${folder}/${clean}.jpg`;
};

// Full-resolution original — preserves the uploaded file extension.
export const pathForMemberOriginal = (name, categories, ext = "jpg") => {
  const clean = name.replace(/Dr\.\s*/g, "").replace(/\s+/g, "");
  let folder = "Volunteers";
  if (categories.includes("Doctor")) folder = "Physicians";
  else if (categories.includes("Admin")) folder = "Admins";
  else if (categories.includes("Lead")) folder = "Leads";
  return `images/${folder}/originals/${clean}.${ext}`;
};

export const uploadTeamImage = async (file, logicalPath) => {
  const app = init();
  const storage = getStorage(app);
  const storagePath = isUat() ? `uat/${logicalPath}` : logicalPath;
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);

  // In UAT, replicate what the Python serverless functions do for prod
  if (isUat()) {
    const db = getFirestore(app);
    const { urls } = getDocNames();
    const key = mangleKey(logicalPath);
    const urlsRef = doc(db, "Data", urls);
    const snap = await getDoc(urlsRef);
    const entry = { [key]: { Path: logicalPath, Url: url } };
    if (snap.exists()) {
      await updateDoc(urlsRef, entry);
    } else {
      await setDoc(urlsRef, entry);
    }
    // Refresh sessionStorage urls
    const fresh = await getDoc(urlsRef);
    if (fresh.exists()) sessionStorage.setItem("urls", JSON.stringify(fresh.data()));
  }

  return logicalPath;
};

// Remove an image from Storage and its entry from the Urls doc (UAT only for Urls).
// Failures are swallowed so a missing/already-deleted file never blocks a save.
export const deleteTeamImage = async (logicalPath) => {
  try {
    const app = init();
    const storage = getStorage(app);
    const storagePath = isUat() ? `uat/${logicalPath}` : logicalPath;
    await deleteObject(ref(storage, storagePath));
  } catch (err) {
    console.warn("deleteTeamImage: storage delete skipped:", logicalPath, err.code);
  }
  if (isUat()) {
    try {
      const app = init();
      const db = getFirestore(app);
      const { urls } = getDocNames();
      const key = mangleKey(logicalPath);
      const urlsRef = doc(db, "Data", urls);
      await updateDoc(urlsRef, { [key]: deleteField() });
      const fresh = await getDoc(urlsRef);
      if (fresh.exists()) sessionStorage.setItem("urls", JSON.stringify(fresh.data()));
    } catch (err) {
      console.warn("deleteTeamImage: Urls cleanup skipped:", logicalPath, err.message);
    }
  }
};

// ─── Seed UAT from Prod (UAT only) ───────────────────────────────────────────

export const seedUatFromProd = async () => {
  const app = init();
  const db = getFirestore(app);
  const storage = getStorage(app);

  const [infoSnap, urlsSnap] = await Promise.all([
    getDoc(doc(db, "Data", "Info")),
    getDoc(doc(db, "Data", "Urls")),
  ]);

  if (!infoSnap.exists() || !urlsSnap.exists()) {
    throw new Error("Prod docs not found — cannot seed UAT.");
  }

  // Phase 1: copy Firestore docs — images immediately display via prod download URLs.
  await Promise.all([
    setDoc(doc(db, "Data", "InfoTest"), infoSnap.data()),
    setDoc(doc(db, "Data", "UrlsTest"), urlsSnap.data()),
  ]);
  sessionStorage.setItem("info", JSON.stringify(infoSnap.data()));
  sessionStorage.setItem("urls", JSON.stringify(urlsSnap.data()));

  // Phase 2: copy each Storage file from prod → uat/ prefix, then update UrlsTest
  // with the UAT download URLs. Falls back gracefully if CORS is not yet configured
  // (images still display via prod URLs from phase 1).
  const urlsData = urlsSnap.data();
  const entries = Object.values(urlsData).filter((e) => e && e.Path && /\.[a-z0-9]+$/i.test(e.Path));
  storage.maxOperationRetryTime = 8000;

  let copied = 0;
  const uatUrlUpdates = {};

  for (const entry of entries) {
    try {
      const blob = await getBlob(ref(storage, entry.Path));
      const uatRef = ref(storage, `uat/${entry.Path}`);
      await uploadBytes(uatRef, blob);
      const uatUrl = await getDownloadURL(uatRef);
      uatUrlUpdates[mangleKey(entry.Path)] = { Path: entry.Path, Url: uatUrl };
      copied++;
    } catch (err) {
      console.warn("seedUatFromProd: skipped", entry.Path, err.message);
    }
  }

  if (Object.keys(uatUrlUpdates).length > 0) {
    const urlsRef = doc(db, "Data", "UrlsTest");
    await updateDoc(urlsRef, uatUrlUpdates);
    const fresh = await getDoc(urlsRef);
    if (fresh.exists()) sessionStorage.setItem("urls", JSON.stringify(fresh.data()));
  }

  return { total: entries.length, copied };
};
